import React from 'react';
import styled from 'styled-components';

import { useTranslation } from '@/app/components/translations/Translation';

import ConditionWrapper from '@/app/components/hotel-detail/ConditionWrapper';
import Tooltip from '@/app/components/hotel-detail/Tooltip';
import { useFetchHotelPromotions } from './hooks/useFetchHotelPromotions';

type BadgeSizeType = 'small' | 'default';

export type DealBadgeProps = {
  size: BadgeSizeType;
} & React.HTMLAttributes<HTMLElement>;

export type DealBadgeComponentProps = {
  backgroundColor?: string;
  textColor?: string;
  description?: string;
  name?: string | React.ReactNode;
  size: BadgeSizeType;
} & React.HTMLAttributes<HTMLElement>;

const StyledTooltip = styled(Tooltip)<{ $size?: BadgeSizeType }>`
  padding: 0;
`;

const Badge = styled.div<{ $backgroundColor?: string; $textColor?: string }>`
  align-items: center;
  background: ${({ $backgroundColor, theme }) => $backgroundColor || theme.kallisto.color.background.static.backgroundBrandPrimary};
  border-radius: 4px;
  color: ${({ $textColor, $backgroundColor, theme }) =>
    $textColor ||
    ($backgroundColor
      ? theme.kallisto.color.text.static.textOnDark
      : theme.kallisto.color.text.static.textBrandContrastPrimary)};
  display: inline-flex;
  font-weight: 300;
  letter-spacing: 0.01rem;
`;

const BadgeDefault = styled(Badge)`
  font-size: ${({ theme }) => theme.kallisto.font.size['175']};
  padding: 0.25rem 0.5rem;
`;

const BadgeSmall = styled(Badge)`
  font-size: ${({ theme }) => theme.kallisto.font.size['150']};
  padding: 0 0.375rem;
`;
const BadgeSizes = {
  default: BadgeDefault,
  small: BadgeSmall,
};

export const DealBadgeComponent = (props: DealBadgeComponentProps) => {
  const { name, description, backgroundColor, textColor, size = 'default', ...rest } = props;

  return name ? (
    <ConditionWrapper
      condition={typeof description !== 'undefined'}
      // eslint-disable-next-line react/no-unstable-nested-components
      match={(children) => (
        <StyledTooltip label={description || ''} {...rest}>
          {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
          <>{children}</>
        </StyledTooltip>
      )}
      {...rest}
    >
      <Badge as={BadgeSizes[size]} $textColor={textColor} $backgroundColor={backgroundColor}>
        {name}
      </Badge>
    </ConditionWrapper>
  ) : null;
};

export function DealBadge(props: DealBadgeProps) {
  const { size, ...rest } = props;
  const { data: promotions } = useFetchHotelPromotions();
  const { t } = useTranslation();

  if (!promotions || !promotions.dynamicDeal?.isDynamicDeal) return null;

  const { name, description, backgroundColor, textColor } = promotions.dynamicDeal;

  return (
    <DealBadgeComponent
      size={size}
      name={name && t(name)}
      description={description}
      backgroundColor={backgroundColor}
      textColor={textColor}
      {...rest}
    />
  );
}
